import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, Image as ImageIcon, Search, Loader2, Maximize2, Minimize2 } from "lucide-react";
import { EmailEditorCore, EmailEditorCoreRef } from "../../../../components/shared/email-editor-core";
import { PlainTextEditor } from "../../../../components/shared/plain-text-editor";
import { EmailTemplateOption } from "./journey-api";

// The bot-studio Send Email action's full editing surface — "Plain Text /
// Email Editor / Templates", reusing the exact same pieces already shared by
// campaign creation and the CRM inbox (EmailEditorCore wraps the same
// react-email-editor drag-and-drop builder; PlainTextEditor is the same rich
// text box) rather than a bot-studio-only copy. Only the Templates gallery
// card is duplicated locally (it's ~20 lines, already duplicated between
// email-content-creation.tsx and here — not worth extracting for two call
// sites).
//
// Per direct instruction: the mockup's "Intuit Mailchimp" branding and
// footer legal text are that design tool's own chrome, not something this
// product ships — the default design below uses neutral placeholders
// instead. There's also no fixed "To" field here (unlike the mockup) since a
// bot action always sends to whoever triggers the journey, never a fixed
// address.

export interface DefaultEmailDesignConfig {
  headerImageUrl?: string;
  bodyHtml: string;
  ctaEnabled?: boolean;
  ctaText?: string;
  ctaColor?: string;
  footerEnabled?: boolean;
  websiteLink?: string;
  instagramLink?: string;
  facebookLink?: string;
  twitterLink?: string;
  footerText?: string;
}

// The "default design" a Plain Text email gets wrapped in before it's ever
// sent (POST /v1/bot/test-run/send's content.email.html_body) — confirmed
// real field, so this needs no backend change: the richer email is just a
// client-composed HTML string handed to that same existing field.
export function buildDefaultEmailHtml(cfg: DefaultEmailDesignConfig): string {
  const header = cfg.headerImageUrl
    ? `<tr><td style="padding:20px 24px 0;"><img src="${cfg.headerImageUrl}" alt="" style="max-width:140px;height:auto;display:block;" /></td></tr>`
    : "";

  const cta =
    cfg.ctaEnabled && cfg.ctaText?.trim()
      ? `<tr><td style="padding:8px 24px 24px;"><a href="#" style="display:inline-block;background:${cfg.ctaColor || "#4f46e5"};color:#ffffff;text-decoration:none;font-weight:600;font-size:14px;padding:11px 22px;border-radius:8px;">${cfg.ctaText}</a></td></tr>`
      : "";

  const socialLinks = [
    cfg.websiteLink && { label: "Website", href: cfg.websiteLink },
    cfg.instagramLink && { label: "Instagram", href: cfg.instagramLink },
    cfg.facebookLink && { label: "Facebook", href: cfg.facebookLink },
    cfg.twitterLink && { label: "X (Twitter)", href: cfg.twitterLink },
  ]
    .filter((l): l is { label: string; href: string } => Boolean(l))
    .map((l) => `<a href="${l.href}" style="margin-right:12px;color:#6b7280;text-decoration:none;font-size:12px;">${l.label}</a>`)
    .join("");

  const footer = cfg.footerEnabled
    ? `<tr><td style="padding:20px 24px;border-top:1px solid #e5e7eb;">
        ${socialLinks ? `<div style="margin-bottom:10px;">${socialLinks}</div>` : ""}
        ${cfg.footerText?.trim() ? `<p style="font-size:11px;color:#9ca3af;margin:0;white-space:pre-line;">${cfg.footerText}</p>` : ""}
      </td></tr>`
    : "";

  return `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="max-width:600px;margin:0 auto;font-family:Arial,Helvetica,sans-serif;background:#ffffff;">
    ${header}
    <tr><td style="padding:20px 24px 4px;font-size:14px;color:#111827;line-height:1.6;">${cfg.bodyHtml || ""}</td></tr>
    ${cta}
    ${footer}
  </table>`;
}

const inputCls =
  "w-full px-3 py-2.5 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-200 dark:focus:ring-indigo-500/20";

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="mb-3.5">
    <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1.5">{label}</label>
    {children}
  </div>
);

const Checkbox: React.FC<{ checked: boolean; onChange: (v: boolean) => void; label: string }> = ({ checked, onChange, label }) => (
  <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-200 py-1 cursor-pointer">
    <input
      type="checkbox"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      className="w-4 h-4 rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500"
    />
    {label}
  </label>
);

// Same CTA/Footer field block used both inline in the Actions panel and
// inside this modal's Plain Text tab — one definition, two call sites.
export const EmailDesignFields: React.FC<{ config: Record<string, any>; onChange: (patch: Record<string, any>) => void }> = ({ config: c, onChange }) => (
  <>
    <Field label="Header image">
      <div className="flex items-center gap-2">
        <div className="w-10 h-10 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 flex items-center justify-center shrink-0 overflow-hidden">
          {c.headerImageUrl ? <img src={c.headerImageUrl} alt="" className="w-full h-full object-cover" /> : <ImageIcon size={16} className="text-gray-300 dark:text-gray-600" />}
        </div>
        <input
          value={c.headerImageUrl || ""}
          onChange={(e) => onChange({ headerImageUrl: e.target.value })}
          placeholder="Paste an image URL"
          className={inputCls}
        />
      </div>
    </Field>

    <div className="mb-1">
      <Checkbox checked={!!c.ctaEnabled} onChange={(v) => onChange({ ctaEnabled: v })} label="Add CTA" />
    </div>
    {c.ctaEnabled && (
      <div className="grid grid-cols-2 gap-3 mb-3.5">
        <Field label="CTA Text">
          <input value={c.ctaText || ""} onChange={(e) => onChange({ ctaText: e.target.value })} placeholder="Click Now" className={inputCls} />
        </Field>
        <Field label="CTA color">
          <input type="color" value={c.ctaColor || "#4f46e5"} onChange={(e) => onChange({ ctaColor: e.target.value })} className="w-full h-[42px] rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-950 cursor-pointer" />
        </Field>
      </div>
    )}

    <div className="mb-1">
      <Checkbox checked={!!c.footerEnabled} onChange={(v) => onChange({ footerEnabled: v })} label="Add Footer" />
    </div>
    {c.footerEnabled && (
      <>
        <Field label="Your Website Link">
          <input value={c.websiteLink || ""} onChange={(e) => onChange({ websiteLink: e.target.value })} placeholder="https://" className={inputCls} />
        </Field>
        <Field label="Instagram Link">
          <input value={c.instagramLink || ""} onChange={(e) => onChange({ instagramLink: e.target.value })} placeholder="https://" className={inputCls} />
        </Field>
        <Field label="Facebook Link">
          <input value={c.facebookLink || ""} onChange={(e) => onChange({ facebookLink: e.target.value })} placeholder="https://" className={inputCls} />
        </Field>
        <Field label="X (Twitter) Link">
          <input value={c.twitterLink || ""} onChange={(e) => onChange({ twitterLink: e.target.value })} placeholder="https://" className={inputCls} />
        </Field>
        <Field label="Footer Text">
          <textarea
            value={c.footerText || ""}
            onChange={(e) => onChange({ footerText: e.target.value })}
            rows={3}
            placeholder="e.g. You're receiving this because you're a valued customer."
            className={`${inputCls} resize-none`}
          />
        </Field>
      </>
    )}
  </>
);

const TemplateCard: React.FC<{ template: EmailTemplateOption; isSelected: boolean; onSelect: () => void }> = ({ template, isSelected, onSelect }) => (
  <button
    type="button"
    onClick={onSelect}
    className={`w-full text-left rounded-xl border-2 overflow-hidden transition-all hover:shadow-md bg-white dark:bg-gray-900 ${
      isSelected ? "border-indigo-500 ring-2 ring-indigo-200" : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
    }`}
  >
    <div className="bg-gray-100 dark:bg-gray-800 h-32 flex items-center justify-center border-b border-gray-200 dark:border-gray-700 relative overflow-hidden">
      {template.snapshot_url ? (
        <img src={template.snapshot_url} alt={template.template_name} className="w-full h-full object-cover" />
      ) : (
        <span className="text-xs text-gray-400 dark:text-gray-500">No preview available</span>
      )}
      {isSelected && (
        <div className="absolute top-2 right-2 w-5 h-5 bg-indigo-500 rounded-full flex items-center justify-center">
          <span className="text-white text-[10px]">✓</span>
        </div>
      )}
    </div>
    <div className="px-3 py-2">
      <p className="text-xs font-medium text-gray-700 dark:text-gray-300 truncate">{template.template_name}</p>
    </div>
  </button>
);

type EmailTab = "plain" | "editor" | "templates";

export const EmailActionModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  config: Record<string, any>;
  onChange: (patch: Record<string, any>) => void;
  emailTemplates: EmailTemplateOption[];
  emailTemplatesLoading: boolean;
  onSelectTemplate: (templateId: string) => void;
}> = ({ isOpen, onClose, config: c, onChange, emailTemplates, emailTemplatesLoading, onSelectTemplate }) => {
  const [tab, setTab] = useState<EmailTab>("plain");
  const [templateSearch, setTemplateSearch] = useState("");
  const editorRef = useRef<EmailEditorCoreRef | null>(null);
  // Per direct ask: same fullscreen-toggle affordance resuable-drawer.tsx
  // gives other drawers in the app (Maximize2/Minimize2, same icons) — this
  // modal is its own hand-rolled drawer rather than that shared component
  // (its content — tabs, the drag-and-drop email editor iframe, template
  // gallery — predates it and isn't a drop-in fit), so the toggle is added
  // locally instead of migrating the whole thing.
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Reset to the Plain Text tab each time the modal opens, same as
  // canva-drawer.tsx's DraftDrawer.
  const prevOpenRef = useRef(false);
  useEffect(() => {
    if (isOpen && !prevOpenRef.current) {
      setTab("plain");
      setIsFullscreen(false);
    }
    prevOpenRef.current = isOpen;
  }, [isOpen]);

  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const handleEditorReady = () => {
    if (!c.customHtml) return;
    setTimeout(() => {
      editorRef.current?.loadDesign({
        counters: { u_column: 1, u_row: 1, u_content_html: 1 },
        body: {
          rows: [
            {
              cells: [1],
              columns: [{ contents: [{ type: "html", values: { html: c.customHtml, containerPadding: "20px" } }], values: { backgroundColor: "", padding: "0px" } }],
              values: { padding: "0px" },
            },
          ],
          values: { backgroundColor: "#ffffff", contentWidth: "600px" },
        },
        schemaVersion: 6,
      });
    }, 300);
  };

  const filteredTemplates = emailTemplates.filter((t) => t.template_name.toLowerCase().includes(templateSearch.toLowerCase()));

  const [everOpened, setEverOpened] = useState(false);
  useEffect(() => {
    if (isOpen) setEverOpened(true);
  }, [isOpen]);
  if (!everOpened) return null;

  return createPortal(
    <>
      <div
        className={`fixed inset-0 bg-black/40 z-[9998] transition-opacity duration-300 ${isOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}
        onClick={onClose}
      />
      <div
        className={`fixed inset-y-0 right-0 w-full ${isFullscreen ? "max-w-full" : "max-w-2xl"} bg-white dark:bg-gray-900 shadow-2xl z-[9999] flex flex-col transition-[transform,max-width] duration-300 ease-in-out ${isOpen ? "translate-x-0" : "translate-x-full"}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-5 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between shrink-0">
          <h2 className="text-base font-bold text-gray-900 dark:text-gray-100">Edit Email</h2>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setIsFullscreen((s) => !s)}
              className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
              title={isFullscreen ? "Exit full screen" : "Full screen"}
            >
              {isFullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
            </button>
            <button type="button" onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800">
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="px-6 pt-4 shrink-0">
          <div className="flex rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700 bg-[#f0f0f8] dark:bg-gray-800 p-1 gap-1">
            {([
              ["plain", "Plain Text"],
              ["editor", "Email Editor"],
              ["templates", "Templates"],
            ] as [EmailTab, string][]).map(([key, label]) => (
              <button
                key={key}
                type="button"
                onClick={() => setTab(key)}
                className={`flex-1 py-2.5 text-sm font-semibold rounded-lg transition-all ${
                  tab === key ? "bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm" : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          {tab === "plain" && (
            <>
              <Field label="Subject">
                <input value={c.title || ""} onChange={(e) => onChange({ title: e.target.value })} placeholder="Enter a subject" className={inputCls} />
              </Field>
              <Field label="Message Body">
                <PlainTextEditor value={c.description || ""} onChange={(v) => onChange({ description: v })} />
              </Field>
              <div className="flex items-center justify-end -mt-2.5 mb-3.5">
                <button
                  type="button"
                  onClick={() => onChange({ description: `${c.description || ""}{{customer.name}}` })}
                  className="text-xs font-semibold text-indigo-600 dark:text-indigo-400 hover:underline"
                >
                  Add variable
                </button>
              </div>
              <EmailDesignFields config={c} onChange={onChange} />
            </>
          )}

          {tab === "editor" && (
            <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden" style={{ minHeight: "560px" }}>
              <EmailEditorCore
                ref={editorRef}
                minHeight="560px"
                onReady={handleEditorReady}
                onChange={(html) => onChange({ customHtml: html, useCustomDesign: true })}
              />
            </div>
          )}

          {tab === "templates" && (
            <>
              <div className="relative mb-4">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  value={templateSearch}
                  onChange={(e) => setTemplateSearch(e.target.value)}
                  placeholder="Search Templates"
                  className={`${inputCls} pl-9`}
                />
              </div>
              {emailTemplatesLoading ? (
                <p className="flex items-center gap-1.5 text-sm text-gray-400 dark:text-gray-500 py-8 justify-center">
                  <Loader2 size={14} className="animate-spin" /> Loading templates…
                </p>
              ) : filteredTemplates.length === 0 ? (
                <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-8">No email templates found for this tenant yet.</p>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  {filteredTemplates.map((t) => (
                    <TemplateCard key={t.id} template={t} isSelected={c.template === t.id} onSelect={() => onSelectTemplate(t.id)} />
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-800 flex gap-2.5 shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-2.5 border border-gray-200 dark:border-gray-700 rounded-xl text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
          >
            Save as Draft
          </button>
          {tab === "templates" ? (
            <button
              type="button"
              disabled={!c.template}
              onClick={onClose}
              className="flex-1 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold rounded-xl disabled:opacity-50"
            >
              Select Template
            </button>
          ) : (
            <button type="button" onClick={onClose} className="flex-1 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold rounded-xl">
              Save Email
            </button>
          )}
        </div>
      </div>
    </>,
    document.body
  );
};
